"use client";

import { createSlice } from "@reduxjs/toolkit";

import { useSelector } from "react-redux";
import { UserDataModel } from "@/libs/auth/redux/auth.types";
import { RootState } from "@/libs/shared/store";
import { settingsApi } from "@/libs/settings/redux/settings.api";
import { trainingPlansApi } from "@/libs/training-plans/redux/trainingPlans.api";
import { nutritionPlansApi } from "@/libs/nutrition-plans/redux/nutrition-plans.api";
import { authApi } from "./auth.api";

type AuthState = {
  user: UserDataModel | null;
  token: string | null;
  apiToken: string | null;
  nutritionApiToken: string | null;
};

const slice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    token: null,
    apiToken: null,
    nutritionApiToken: null,
  } as AuthState,
  reducers: {
    logout: (state) => {
      state.token = null;
      state.user = null;
      state.apiToken = null;
      state.nutritionApiToken = null;
    },
    setNutritionApiToken: (state, action) => {
      state.nutritionApiToken = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      authApi.endpoints.login.matchFulfilled,
      (state, { payload }) => {
        state.token = payload.idToken;
        state.user = payload;
      },
    );
    builder.addMatcher(
      nutritionPlansApi.endpoints.generateToken.matchFulfilled,
      (state, { payload }) => {
        state.nutritionApiToken = payload.token;
      },
    );
    builder.addMatcher(
      authApi.endpoints.register.matchFulfilled,
      (state, { payload }) => {
        state.apiToken = payload.idToken;
      },
    );
    builder.addMatcher(
      authApi.endpoints.completeRegistration.matchFulfilled,
      (state, { payload }) => {
        state.token = payload.idToken;
        state.user = payload;
      },
    );
    builder.addMatcher(
      settingsApi.endpoints.getProfile.matchFulfilled,
      (state, { payload }) => {
        state.user = {
          ...state.user,
          ...payload,
        };
      },
    );
    builder.addMatcher(
      settingsApi.endpoints.getPersonalData.matchFulfilled,
      (state, { payload }) => {
        state.user = {
          ...state.user,
          ...payload,
        };
      },
    );
    builder.addMatcher(
      settingsApi.endpoints.getPreferences.matchFulfilled,
      (state, { payload }) => {
        state.user = {
          ...state.user,
          ...payload,
        };
      },
    );
    builder.addMatcher(
      trainingPlansApi.endpoints.updateShowBaseExercises.matchFulfilled,
      (state, { payload }) => {
        state.user = {
          ...(state.user as UserDataModel),
          show_base_exercises: Number(payload),
        };
      },
    );
    builder.addMatcher(
      settingsApi.endpoints.getChatEnabled.matchFulfilled,
      (state, { payload: cometChatUserId }) => {
        if (
          (state.user as UserDataModel)?.comet_chat_user_id !== cometChatUserId
        ) {
          state.user = {
            ...(state.user as UserDataModel),
            comet_chat_user_id:
              typeof cometChatUserId === "string" ? cometChatUserId : null,
          };
        }
      },
    );
  },
});

export const authReducer = slice.reducer;
export const { logout, setNutritionApiToken } = slice.actions;

export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectTokenJWT = (state: RootState) => state.auth.token;

export const useSelectCurrentUser = () => useSelector(selectCurrentUser);
